import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Radio, Loader2, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { authAPI } from '../lib/api';

export default function ForgotPasswordPage() {
  const { theme } = useApp();
  const primary = theme?.primary || '#2563eb';

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError('Informe seu e-mail.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await authAPI.forgotPassword(email);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao solicitar redefinição.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: 'var(--bg)',
        backgroundImage: `radial-gradient(ellipse 80% 50% at 50% -20%, ${primary}22 0%, transparent 70%)`,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex flex-col items-center gap-3">
            <div
              className="h-14 w-14 rounded-2xl flex items-center justify-center shadow-lg"
              style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}bb 100%)` }}
            >
              <Radio size={26} className="text-white" />
            </div>
            <span className="font-display font-black text-xl tracking-tight text-[var(--text-primary)]">
              OLT MANAGER
            </span>
          </Link>
        </div>

        <div className="rounded-2xl border border-[var(--border-mid)] bg-[var(--surface)] shadow-2xl p-8">
          {result ? (
            <div className="text-center">
              <CheckCircle2 size={48} className="mx-auto mb-4" style={{ color: primary }} />
              <h2 className="text-lg font-bold text-[var(--text-primary)] mb-2">Solicitação enviada!</h2>
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                {result.message}
              </p>
              {result.reset_token && (
                <div className="mt-4 p-4 rounded-xl bg-[var(--surface-2)] border border-[var(--border-mid)] text-left">
                  <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2">
                    Token de redefinição (modo desenvolvimento)
                  </p>
                  <code className="text-xs break-all text-[var(--text-primary)] font-mono">
                    {result.reset_token}
                  </code>
                  <p className="text-xs text-[var(--text-muted)] mt-2">
                    Acesse{' '}
                    <Link
                      to={`/reset-password?token=${result.reset_token}`}
                      className="font-semibold hover:underline"
                      style={{ color: primary }}
                    >
                      redefinir senha
                    </Link>
                    {' '}para continuar.
                  </p>
                </div>
              )}
              <Link
                to="/login"
                className="mt-6 inline-flex items-center gap-2 text-sm font-medium hover:underline"
                style={{ color: primary }}
              >
                <ArrowLeft size={14} /> Voltar para o login
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-xl font-bold text-[var(--text-primary)]">Recuperar senha</h1>
                <p className="text-sm text-[var(--text-secondary)] mt-1">
                  Informe seu e-mail para receber as instruções de redefinição.
                </p>
              </div>

              {error && (
                <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-500/10 border border-red-500/20 p-3 text-sm text-red-400">
                  <AlertCircle size={15} className="shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-[var(--text-secondary)] mb-1.5 uppercase tracking-wider">
                    E-mail
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="seu@email.com"
                    className="w-full h-11 px-4 rounded-xl border border-[var(--border-mid)] bg-[var(--surface-2)] text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-2 transition-all"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-opacity disabled:opacity-60"
                  style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}dd 100%)` }}
                >
                  {loading && <Loader2 size={16} className="animate-spin" />}
                  {loading ? 'Enviando...' : 'Enviar instruções'}
                </button>
              </form>

              <p className="mt-6 text-center text-xs">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
                >
                  <ArrowLeft size={12} /> Voltar para o login
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
